import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, Typography } from 'antd';
import { DeleteOutlined, SelectOutlined } from '@ant-design/icons';

import { UsersAutocomplete } from 'modules';
import { IconButton, Button } from 'components';

import useValidation from 'hooks/forms/useValidation';

import { publicUrls } from 'constants/urls';

import { IdType } from 'types/common';
import { IBeneficiariesListProps } from './BeneficiariesList.types';

import './BeneficiariesList.styles.scss';

const MAX_BENEFICIARIES_COUNT = 3;

const { Text } = Typography;

const BeneficiariesList = ({ value, ...props }: IBeneficiariesListProps) => {
  const { t } = useTranslation();
  const rules = useValidation();

  const getUserUrl = (uid: IdType) => {
    return uid ? `${publicUrls.main}?id=${uid}` : 'javascript:';
  };

  return (
    <Form.List name={['beneficiaries']} rules={rules.beneficiaries as any} {...props}>
      {(fields, { add, remove }, { errors }) => (
        <div className="np-beneficiaries-list">
          <div className="np-beneficiaries-list__header">
            <Text>{t('form.label.beneficiaries')}</Text>
          </div>

          {fields.map(field => (
            <Row key={field.key} gutter={[8, 8]} className="np-beneficiaries-list__item">
              <Col flex={1}>
                <Form.Item name={[field.name]} rules={[rules.requiredKey('uid')]}>
                  <UsersAutocomplete fullUser />
                </Form.Item>
              </Col>

              <Col className="np-beneficiaries-list__icon-button icon-size-14">
                <Link to={getUserUrl(value?.[field.name].uid)} target="_blank" rel="noopener noreferrer">
                  <IconButton
                    color="primary"
                    disabled={value && !value?.[field.name].uid}
                    icon={<SelectOutlined />}
                    tooltip={t('tooltip.details')}
                  />
                </Link>
              </Col>

              <Col className="np-beneficiaries-list__icon-button">
                <IconButton
                  color="red"
                  icon={<DeleteOutlined />}
                  tooltip={t('tooltip.remove')}
                  onClick={() => remove(field.name)}
                />
              </Col>
            </Row>
          ))}

          <Form.ErrorList errors={errors} />

          {fields.length < MAX_BENEFICIARIES_COUNT && (
            <Button type="link" onClick={() => add({})} className="np-beneficiaries-list__button">
              {t('form.button.addBeneficiary')}
            </Button>
          )}
        </div>
      )}
    </Form.List>
  );
};

export default BeneficiariesList;
